import { AxiosResponse } from 'axios';
import $api from '@/http/index';
import {
    IDictionaryDetailedItems,
    IDictionaryGridScheme,
    IDictionaryListItem,
} from '@models/dictionary/IDictionaryDetailedItems';
import { IDictionaryData, IDictionaryGridData } from '@models/dictionary/IDictionaryData';
import { IField } from '@models/IFormData';
import { getLoadOptionsQuery } from '@utils/loadOptionsConverter';
import { IExternalSearchScheme } from '@models/dictionary/IExternalSearchScheme';
import { IAddFormScheme } from '@models/dictionary/IAddFormScheme';
import { IGridResponse } from '@models/response/IGridResponse';
import { IDataGridDescriptionResponse } from '@models/response/IDataGridDescriptionResponse';
import { IGridExportSettings } from '@models/response/IGridExportSettings';
import { IDataGridService } from './IDataGridService';
import { IDocumentResult } from '@models/document/IDocumentResult';
import { FilesService } from './FilesService';
import { invalidateDictionaryItems } from './dictionaryCache';

export interface IDictionarySqlImportResult {
    success: boolean;
    totalRows: number;
    inserted: number;
    updated: number;
    skipped: number;
    errors: IDictionarySqlImportError[];
}

export interface IDictionarySqlImportError {
    rowNumber: number;
    columnKey: string;
    message: string;
}

export interface ITreeItemsLoadRequest {
    options: Record<string, string>;
    parentId?: string;
    formValues?: string;
    predicates?: string;
    loadMode?: string;
}

export class DictionariesService {
    static async getItems(
        dictName: string,
        parentItemId: string[] | undefined,
        options: any,
    ): Promise<AxiosResponse<IDictionaryListItem[]>> {
        let parentIds = parentItemId === undefined ? '' : parentItemId.map((x) => `&parentIds=${x}`).join('');
        return $api.get<IDictionaryListItem[]>(
            `/dictionaries/items/${dictName}?${getLoadOptionsQuery(options)}${parentIds}`,
        );
    }

    static async getPlainItems(
        dictName: string,
        options: any,
        signal?: AbortSignal,
    ): Promise<AxiosResponse<IGridResponse>> {
        return $api.get<IGridResponse>(`/dictionaries/getPlainItems/${dictName}?${getLoadOptionsQuery(options)}`, {
            signal,
        });
    }

    static async fetchDictionaryScheme(
        dictName: string,
        signal?: AbortSignal,
    ): Promise<AxiosResponse<IDictionaryDetailedItems>> {
        return $api.get<IDictionaryDetailedItems>(`/dictionaries/scheme/${dictName}`, { signal });
    }

    static async fetchExternalSearchScheme(searchFormName: string): Promise<AxiosResponse<IExternalSearchScheme>> {
        return $api.get<IExternalSearchScheme>(`/dictionaries/exsearchscheme/${searchFormName}`);
    }

    static async fetchAddFormScheme(searchFormName: string): Promise<AxiosResponse<IAddFormScheme>> {
        return $api.get<IExternalSearchScheme>(`/dictionaries/addformscheme/${searchFormName}`);
    }

    static async fetchFormdataDictionaryGridScheme(dictName: string): Promise<AxiosResponse<IDictionaryGridScheme>> {
        return $api.get<IDictionaryGridScheme>(`/dictionaries/gridFormdataScheme/${dictName}`);
    }
    static async fetchDictionaryGridScheme(dictName: string): Promise<AxiosResponse<IDictionaryGridScheme>> {
        return $api.get<IDictionaryGridScheme>(`/dictionaries/gridScheme/${dictName}`);
    }
    static async getGridFormdataItems(
        docId: string,
        dictName: string,
        loadOptions: any,
    ): Promise<AxiosResponse<IDictionaryGridData>> {
        return $api.get<IDictionaryGridData>(`/dictionaries/gridFormdataItems/${docId}/${dictName}${loadOptions}`);
    }
    static async getTreeItems(
        dictName: string,
        body: ITreeItemsLoadRequest,
    ): Promise<AxiosResponse<IDictionaryGridData>> {
        return $api.post<IDictionaryGridData>(`/dictionaries/tree/items/${dictName}`, body);
    }
    static async getGridItems(
        dictName: string,
        loadOptions: any,
        signal?: AbortSignal,
    ): Promise<AxiosResponse<IDictionaryGridData>> {
        return $api.get<IDictionaryGridData>(`/dictionaries/gridItems/${dictName}${loadOptions}`, { signal });
    }

    static async getItem(dictName: string, itemId: string): Promise<AxiosResponse<IDictionaryData>> {
        return $api.get<IDictionaryData>(`/dictionaries/data/${dictName}/${itemId}`);
    }
    static async getNewItem(
        dictName: string,
        structIds: string[],
        parentId?: string,
    ): Promise<AxiosResponse<IDictionaryData>> {
        return $api.get<IDictionaryData>(`/dictionaries/newData/${dictName}/${parentId ?? ''}`, {
            params: {
                subDictKeys: structIds,
            },
        });
    }
    static async getItemByCode(dictName: string, code: string): Promise<AxiosResponse<IDictionaryData>> {
        return $api.get<IDictionaryData>(`/dictionaries/itemByCode/${dictName}/${code}`);
    }
    static async getSubTreeByCode(dictName: string, code: string): Promise<AxiosResponse<Record<string, any>>> {
        return $api.get<Record<string, any>>(`/dictionaries/SubTreeByCode/${dictName}/${code}`);
    }
    static async getItemsByCode(dictName: string, codes: string[]): Promise<AxiosResponse<IDictionaryData[]>> {
        return $api.get<IDictionaryData[]>(`/dictionaries/itemsByCode/${dictName}/${codes.join(',')}`);
    }

    static async getTreeItemsByCode(dictName: string, codes: string[]): Promise<AxiosResponse<IDictionaryData[]>> {
        return $api.post<IDictionaryData[]>(`/dictionaries/treeItemsByCode/${dictName}`, codes);
    }

    static async getItemByFilter(
        dictName: string,
        dictColumn: string,
        code: string,
        type: string,
    ): Promise<AxiosResponse<IDictionaryData>> {
        return $api.get<IDictionaryData>(`/dictionaries/itemByFilter/${dictName}/${dictColumn}/${code}/${type}`);
    }
    static async deleteItem(dictName: string, itemId: string): Promise<AxiosResponse<void>> {
        return $api
            .delete(`/dictionaries/data/${dictName}/${itemId}`)
            .finally(() => invalidateDictionaryItems(dictName));
    }

    static async updateItem(
        dictName: string,
        itemId: string,
        attributes: IField[],
        structIds: string[],
    ): Promise<AxiosResponse<void>> {
        return $api
            .patch(`/dictionaries/data/${dictName}/${itemId}`, attributes, { params: { subDictKeys: structIds } })
            .finally(() => invalidateDictionaryItems(dictName));
    }
    static async addItem(
        dictName: string,
        attributes: IField[],
        structIds: string[],
        parentId?: string,
    ): Promise<AxiosResponse<void>> {
        return $api
            .post(`/dictionaries/data/${dictName}/${parentId ?? ''}`, attributes, {
                params: { subDictKeys: structIds },
            })
            .finally(() => invalidateDictionaryItems(dictName));
    }

    static async getAllDictsItems(
        dictNames: string[],
    ): Promise<AxiosResponse<{ [dictName: string]: IDictionaryData[] }>> {
        return $api.get<{ [dictName: string]: IDictionaryData[] }>(`/dictionaries/getAllItems`, {
            params: {
                dictNames: dictNames,
            },
        });
    }

    static async fetchExternalSearch(searchApiUrl: string, formData: any): Promise<AxiosResponse<any>> {
        return $api.post<any>(`${searchApiUrl}`, formData);
    }

    static async saveExternalSearch(saveApiUrl: string, selectItem: any): Promise<AxiosResponse<any>> {
        return $api.post<any>(`${saveApiUrl}`, selectItem);
    }

    static async fetchSqlDictionaryGridScheme(
        templateName: string,
    ): Promise<AxiosResponse<IDataGridDescriptionResponse>> {
        return $api.get<IDataGridDescriptionResponse>(`/dictionaries/sql/scheme/${templateName}`);
    }

    static async getSqlItem(templateName: string, key: string): Promise<AxiosResponse<IDictionaryData>> {
        return $api.get<IDictionaryData>(`/dictionaries/sql/item/${templateName}/${encodeURIComponent(key)}`);
    }

    static async getNewSqlItem(templateName: string): Promise<AxiosResponse<IDictionaryData>> {
        return $api.get<IDictionaryData>(`/dictionaries/sql/newData/${templateName}`);
    }

    static async addSqlItem(templateName: string, attributes: IField[]): Promise<AxiosResponse<string>> {
        return $api.post<string>(`/dictionaries/sql/item/${templateName}`, attributes);
    }

    static async updateSqlItem(templateName: string, key: string, attributes: IField[]): Promise<AxiosResponse<void>> {
        return $api.patch(`/dictionaries/sql/item/${templateName}/${encodeURIComponent(key)}`, attributes);
    }

    static async deleteSqlItem(templateName: string, key: string): Promise<AxiosResponse<void>> {
        return $api.delete(`/dictionaries/sql/item/${templateName}/${encodeURIComponent(key)}`);
    }

    static async exportSqlItems(templateName: string, options: any, columns: IGridExportSettings[]): Promise<void> {
        await FilesService.downloadFilePost(`dictionaries/sql/export/${templateName}`, { options, columns });
    }

    static async downloadSqlImportTemplate(templateName: string): Promise<void | AxiosResponse> {
        return FilesService.downloadFile(`dictionaries/sql/import-template/${templateName}`);
    }

    static async importSqlItems(templateName: string, file: File): Promise<AxiosResponse<IDictionarySqlImportResult>> {
        const formData = new FormData();
        formData.append('file', file);
        return $api.post<IDictionarySqlImportResult>(`/dictionaries/sql/import/${templateName}`, formData);
    }
}

export class DictionarySqlGridService extends IDataGridService {
    putData(_data: any): Promise<AxiosResponse<IDocumentResult, any>> {
        throw new Error('Method not implemented.');
    }

    async fetchScheme(data: string): Promise<AxiosResponse<IDataGridDescriptionResponse, any>> {
        return DictionariesService.fetchSqlDictionaryGridScheme(data);
    }

    async fetchData(data: string, loadOptions: any, signal?: AbortSignal): Promise<AxiosResponse<IGridResponse, any>> {
        return $api.post<IGridResponse>(`/dictionaries/sql/items/${data}`, loadOptions, { signal });
    }

    fetchDetailData(_data: any, _detailData: any, _loadOptions: any): Promise<AxiosResponse<IGridResponse, any>> {
        throw new Error('Method not implemented.');
    }

    exportData(template: string, options: any, columns: IGridExportSettings[]): Promise<void> {
        return DictionariesService.exportSqlItems(template, options, columns);
    }
}
